import { useMemo } from 'react';
import { useMasterData } from './useMasterData';
import { useAuth } from './useAuth';
import { useCaseHelpers } from './useCaseHelpers';
import {
  getCaseType,
  getTimeInfo,
  sortCasesByTime,
  filterScheduleCases,
  analyzeScheduleProgress,
} from '../utils/scheduleUtils';

/**
 * 陸送員専用スケジュール管理フック
 * 陸送員にとって最適化されたスケジュール情報を提供
 * 管理者モードでは全案件を表示
 */
export const useDriverSchedule = (
  selectedDate: string,
  viewMode: 'driver' | 'admin' = 'driver'
) => {
  const { cases } = useMasterData();
  const { user } = useAuth();
  const helpers = useCaseHelpers();

  // 表示対象の案件を取得（モードに応じて切り替え）
  const targetCases = useMemo(() => {
    if (viewMode === 'admin') {
      // 管理者モード: 全ての割り当て済み案件を表示
      return cases.filter(case_ => case_.assignedLandTransporterId);
    } else {
      // 陸送員モード: 自分に割り当てられた案件のみ
      if (!user?.landTransporterId) return [];
      return cases.filter(case_ => case_.assignedLandTransporterId === user.landTransporterId);
    }
  }, [cases, user?.landTransporterId, viewMode]);

  // 選択日の案件（引取日または継続中案件）
  const todaysCases = useMemo(() => {
    return filterScheduleCases(targetCases, selectedDate);
  }, [targetCases, selectedDate]);

  // 時系列順でソート済み案件
  const sortedCases = useMemo(() => {
    return sortCasesByTime(todaysCases);
  }, [todaysCases]);

  // 案件の進行状況分析
  const scheduleAnalysis = useMemo(() => {
    return analyzeScheduleProgress(todaysCases);
  }, [todaysCases]);

  // 次の案件を取得
  const nextCase = useMemo(() => {
    return sortedCases.find(c => c.status === 'assigned');
  }, [sortedCases]);

  // 現在進行中の案件を取得
  const currentCase = useMemo(() => {
    return sortedCases.find(c => c.status === 'in_transit');
  }, [sortedCases]);

  // 陸送員向けの案件情報に表示用プロパティを付加
  const casesWithDisplayInfo = useMemo(() => {
    return sortedCases.map(case_ => {
      return {
        ...case_,
        // 表示用の便利プロパティ
        // FL-166: スケジュール画面用に車両モデル名のみ表示（メーカー名を除外）
        vehicleName: helpers.safe.getVehicleNameForSchedule(case_),
        pickupPartnerName: helpers.safe.getPickupPartnerName(case_),
        deliveryPartnerName: helpers.safe.getDeliveryPartnerName(case_),
        billingPartnerName: helpers.safe.getBillingPartnerName(case_),

        // FL-188: 陸送員スケジュール表示項目の拡充
        pickupPrefecture: helpers.safe.getPickupPrefecture(case_), // 地域
        vehiclePlateNumber: case_.vehiclePlateNumber || '未設定', // ナンバープレート
        vehicleNumber: case_.vehicleNumber || '未設定', // 車体番号（車台番号）
        chassisNumber: case_.chassisNumber || '未設定', // FL-191: 車体番号

        // 案件タイプ判定
        caseType: getCaseType(case_, selectedDate),

        // 時間情報
        timeInfo: getTimeInfo(case_, selectedDate),
      };
    });
  }, [sortedCases, selectedDate, helpers.safe]);

  return {
    // 基本データ
    todaysCases: casesWithDisplayInfo,
    sortedCases: casesWithDisplayInfo,

    // 重要案件
    nextCase: nextCase ? casesWithDisplayInfo.find(c => c.id === nextCase.id) : undefined,
    currentCase: currentCase ? casesWithDisplayInfo.find(c => c.id === currentCase.id) : undefined,

    // 分析データ
    scheduleAnalysis,

    // ステータス
    hasSchedule: todaysCases.length > 0,
    isEmpty: todaysCases.length === 0,
  };
};
